import { useEffect, useRef, useState } from 'react';
import type { CupoTaquilla } from '../../api/ops/eventos';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Campo } from '../../componentes/Campo';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Interruptor } from '../../componentes/Interruptor';
import { OpcionRadio } from '../../componentes/OpcionRadio';
import { avisoBreveDeError } from '../../logica/estados';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPestana } from './Ficha';
import { hayVendidas } from './formulario';
import { Aviso, Ceja, CampoDiaHora, Lineas, TarjetaAforo } from './piezas';
import { cupoYaRepartido, diaYHoraEn, EDADES, MODO, MODOS, plazoVigente, rangoEn, requisitos, textoEdad, textoErrorGuardar } from './reglas';
import { ListaTipos } from './TiposEntrada';

/** Pestañas Datos, Entradas, Puerta y Publicar de la ficha (10.2 y 10.3). */

export function Datos({ api, negocio, datos, form, setForm, errores, edita, sedes }: PropsPestana) {
    const e = datos.evento;
    const avisar = useAvisoBreve();
    const archivo = useRef<HTMLInputElement>(null);
    // El cartel guardado se pinta con `image_url` (G836); uno recién subido, con el de `uploadEventPoster`.
    const [vista, setVista] = useState<string | null>(e.image_url ?? null);
    const [subiendo, setSubiendo] = useState(false);
    const plazo = plazoVigente(e.date_changes);

    const subir = (f: File) => {
        setSubiendo(true);
        // G858: el cartel lo sube Eventos (`uploadEventPoster`, con `entradas.eventos.gestionar`), ya recortado a 4:5.
        api.uploadEventPoster(negocio, f, f.name)
            .then((m) => {
                setSubiendo(false);
                setVista(m.image_url);
                setForm({ ...form, cartel: m.image_ref });
            })
            .catch((err: unknown) => {
                setSubiendo(false);
                avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? (err instanceof ErrorApi && err.estado === 422 ? 'Esa imagen no vale: usa PNG, JPG o WebP de 5 MB como mucho.' : textoErrorGuardar(err)) });
            });
    };

    return (
        <div className="rv-ev-form">
            {plazo !== null && (
                <Aviso tono="amarillo" icono="calendar-clock" titulo={`Fecha cambiada: antes era el ${diaYHoraEn(plazo.previous_starts_at, e.timezone).toLocaleLowerCase('es')}.`}>
                    {`Quien tiene entrada, invitación o reservado puede cancelar gratis hasta el ${diaYHoraEn(plazo.free_cancellation_until, e.timezone).toLocaleLowerCase('es')}.`}
                </Aviso>
            )}
            <Campo etiqueta="Nombre del evento" valor={form.nombre} onChange={(v) => { setForm({ ...form, nombre: v }); }} error={errores.name} bloqueado={!edita} max={120} />
            <Campo etiqueta="Descripción" valor={form.descripcion} onChange={(v) => { setForm({ ...form, descripcion: v }); }} error={errores.description} ayuda="La ven tus clientes en la web y en la ficha del evento." bloqueado={!edita} filas={3} max={2000} />
            <Desplegable etiqueta="Sede" valor={form.sede} opciones={sedes.some((s) => s.id === form.sede) ? sedes.map((s) => ({ valor: s.id, texto: s.nombre })) : [{ valor: form.sede, texto: 'Sede archivada' }, ...sedes.map((s) => ({ valor: s.id, texto: s.nombre }))]} onChange={(v) => { setForm({ ...form, sede: v }); }} error={errores.location_id} bloqueado={!edita} />

            <Ceja>Cartel</Ceja>
            <div className="rv-ev-cartel-fila">
                <span className="rv-ev-cartel rv-ev-cartel--grande" aria-hidden="true">
                    {vista !== null && form.cartel !== null ? <img src={vista} alt="" /> : form.cartel !== null ? <Icono nombre="image" /> : (form.nombre.slice(0, 1) || '?').toLocaleUpperCase('es')}
                </span>
                <span className="rv-en-celda">
                    <strong>{form.cartel === null ? 'Sin cartel' : 'Cartel puesto'}</strong>
                    <small>Vertical 4:5, PNG, JPG o WebP hasta 5 MB. Sale en la cartelera, la página del evento y la lista.</small>
                    {edita && (
                        <span className="rv-ev-botones">
                            <Boton tipo="secundario" icono="upload" ocupado={subiendo} ocupadoTexto="Subiendo…" onClick={() => { archivo.current?.click(); }}>
                                {form.cartel === null ? 'Subir el cartel' : 'Cambiar'}
                            </Boton>
                            {form.cartel !== null && (
                                <Boton tipo="fantasma" icono="x" onClick={() => { setVista(null); setForm({ ...form, cartel: null }); }}>
                                    Quitar
                                </Boton>
                            )}
                        </span>
                    )}
                </span>
                <input
                    ref={archivo}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    hidden
                    aria-label="Cartel del evento"
                    onChange={(ev) => {
                        const f = ev.target.files?.[0];
                        ev.target.value = '';
                        if (f !== undefined) {
                            subir(f);
                        }
                    }}
                />
            </div>

            <Ceja>Fechas</Ceja>
            {e.status === 'draft' ? (
                <div className="rv-ca-dos">
                    <CampoDiaHora etiqueta="Empieza" valor={form.inicio} onChange={(v) => { setForm({ ...form, inicio: v }); }} error={errores.starts_at} bloqueado={!edita} />
                    <CampoDiaHora etiqueta="Termina" valor={form.fin} onChange={(v) => { setForm({ ...form, fin: v }); }} error={errores.ends_at} bloqueado={!edita} />
                </div>
            ) : (
                <p className="rv-ev-texto">
                    <strong>{rangoEn(e.starts_at, e.ends_at, e.timezone)}</strong>
                    {e.status === 'published' && <span> · Publicado, la fecha se cambia en «Fecha».</span>}
                </p>
            )}

            <Ceja>Quién lo ve</Ceja>
            <div className="rv-ev-opciones rv-ev-opciones--dos" role="radiogroup" aria-label="Quién lo ve">
                <OpcionRadio elegida={form.visibilidad === 'public'} bloqueada={!edita} onElegir={() => { setForm({ ...form, visibilidad: 'public' }); }} icono="globe" titulo="Pública" texto="En tu web, el widget y el asistente." />
                <OpcionRadio elegida={form.visibilidad === 'unlisted'} bloqueada={!edita} onElegir={() => { setForm({ ...form, visibilidad: 'unlisted' }); }} icono="link" titulo="Solo con enlace" texto="No sale en la cartelera; se compra con el enlace." />
            </div>

            <Ceja>Edad</Ceja>
            <Desplegable
                etiqueta="Aviso de edad"
                valor={form.edad}
                opciones={[{ valor: '', texto: textoEdad(null) }, ...EDADES.map((n) => ({ valor: String(n), texto: textoEdad(n) })), ...(form.edad !== '' && !EDADES.includes(Number(form.edad)) ? [{ valor: form.edad, texto: textoEdad(Number(form.edad)) }] : [])]}
                onChange={(v) => { setForm({ ...form, edad: v }); }}
                error={errores.age_notice_min_age}
                bloqueado={!edita}
                ayuda={form.edad === '' ? 'Si lo eliges, sale en la página del evento y al comprar. La edad se comprueba en la puerta.' : 'Sale en la página del evento y al comprar. Solo informa: la edad se comprueba en la puerta.'}
            />
        </div>
    );
}

export function Entradas(props: PropsPestana) {
    const { datos, form, setForm, errores, edita } = props;
    const e = datos.evento;
    const bloqueado = !edita || hayVendidas(e);
    // G786: con entradas vendidas o pendientes, tocar otro «cómo se entra» dice por qué no se cambia (como el `409 sale_mode_locked`).
    const [modoTocado, setModoTocado] = useState(false);
    return (
        <div className="rv-ev-form">
            <Ceja>Cómo se entra</Ceja>
            <div className="rv-ev-opciones rv-ev-opciones--tres" role="radiogroup" aria-label="Cómo se entra">
                {MODOS.map((m) => (
                    <OpcionRadio key={m} elegida={form.modo === m} bloqueada={bloqueado} onBloqueada={edita && hayVendidas(e) ? () => { setModoTocado(true); } : undefined} onElegir={() => { setForm({ ...form, modo: m }); }} titulo={MODO[m][0]} texto={MODO[m][1]} />
                ))}
            </div>
            {errores.sale_mode !== undefined ? <span className="rv-ev-error" role="alert">{errores.sale_mode}</span> : modoTocado && <span className="rv-ev-error" role="alert">Con entradas vendidas no se cambia cómo se entra.</span>}
            <Interruptor
                etiqueta="Entradas nominativas"
                icono="user-check"
                valor={form.nominativas}
                onChange={(v) => { setForm({ ...form, nominativas: v }); }}
                bloqueado={bloqueado}
                ayuda={hayVendidas(e) ? 'Con entradas vendidas ya no se cambia.' : 'Se pide el nombre de cada asistente; todas van al comprador.'}
            />
            <div className="rv-ev-medio">
                <Campo etiqueta="Máximo de entradas por pedido" tipo="number" modo="numeric" valor={form.maximo} onChange={(v) => { setForm({ ...form, maximo: v }); }} error={errores.max_tickets_per_order} ayuda="Entre 1 y 100." bloqueado={!edita} />
            </div>
            {e.sale_mode !== 'lists_only' && <ListaTipos {...props} />}
        </div>
    );
}

export function Puerta({ api, negocio, datos, form, setForm, errores, edita, permisos, soloLectura, navegar, recargarFicha }: PropsPestana) {
    const e = datos.evento;
    const { puede } = usePermisos();
    const avisar = useAvisoBreve();
    const [preparando, setPreparando] = useState(false);
    const [cupo, setCupo] = useState<CupoTaquilla | null>(null);
    // 10.2 › Puerta (G786): lo que dijo «Preparar ahora», en su línea; el `409` (el cupo baja de lo que ya tienen las tablets) también.
    const [resultado, setResultado] = useState<{ ok: boolean; texto: string } | null>(null);
    const preparar = () => {
        setPreparando(true);
        setResultado(null);
        api.prepareEventDoor(negocio, e.id)
            .then((c) => {
                setPreparando(false);
                setCupo(c);
                setResultado({ ok: true, texto: 'Puerta preparada. Las tablets ya pueden bajar los pases y su parte del cupo.' });
                recargarFicha();
            })
            .catch((err: unknown) => {
                setPreparando(false);
                const breve = avisoBreveDeError(err);
                const repartido = cupoYaRepartido(err);
                if (repartido !== null) {
                    // G948: `409 door_quota_below_assigned` con la cifra de lo repartido entre tablets (texto del dibujo 10.2).
                    setResultado({ ok: false, texto: `No puede bajar de ${String(repartido)}: ya se lo han llevado las tablets. Pon un cupo de taquilla de ${String(repartido)} o más.` });
                } else if (breve !== null) {
                    avisar({ tipo: 'error', texto: breve });
                } else {
                    setResultado({ ok: false, texto: textoErrorGuardar(err) });
                }
            });
    };
    const reservado = cupo === null ? e.capacity_detail.box_office_reserved : cupo.quantity - cupo.sold - cupo.returned;
    const lineas = [];
    if (e.status === 'published' && permisos.preparaPuerta) {
        lineas.push({
            icono: 'wifi-off' as const,
            titulo: 'Puerta sin conexión',
            texto: (
                <>
                    <span>{reservado > 0 ? `${String(reservado)} entradas de taquilla reservadas para vender sin conexión.` : `Reserva el cupo de taquilla (${String(e.box_office_quota ?? 0)}) y descarga los pases en la tablet.`}</span>
                    <span className="rv-ev-botones">
                        {!soloLectura && (
                            <Boton tipo="secundario" icono="wifi-off" ocupado={preparando} ocupadoTexto="Preparando…" onClick={preparar}>
                                Preparar ahora
                            </Boton>
                        )}
                        <Boton tipo="secundario" icono="door-open" onClick={() => { navegar(`/puerta?apartado=preparar&evento=${encodeURIComponent(e.id)}`); }}>
                            Ver la puerta
                        </Boton>
                    </span>
                    {resultado !== null && <span className={resultado.ok ? 'rv-ev-texto' : 'rv-ev-error'} role={resultado.ok ? 'status' : 'alert'}>{resultado.texto}</span>}
                </>
            ),
        });
    }
    if (e.status === 'published' && puede('entradas.taquilla.vender') && e.sale_mode !== 'lists_only') {
        lineas.push({
            icono: 'ticket' as const,
            titulo: 'Taquilla',
            texto: (
                <>
                    <span>Vende en la puerta con efectivo o el datáfono del negocio; solo se anota la forma de pago.</span>
                    <span className="rv-ev-botones">
                        <Boton tipo="secundario" icono="arrow-right" onClick={() => { navegar(`/taquilla?evento=${encodeURIComponent(e.id)}`); }}>
                            Abrir la taquilla
                        </Boton>
                    </span>
                </>
            ),
        });
    }
    if (e.status === 'draft') {
        lineas.push({ icono: 'info' as const, titulo: 'La puerta', texto: 'Al publicar se prepara el grupo de acceso: los pases de las entradas y las listas valen en la puerta.' });
    }
    return (
        <div className="rv-ev-form">
            <TarjetaAforo aforo={e.capacity_detail} titulo="Aforo ahora" invitados={datos.invitados?.people ?? null} />
            <div className="rv-ca-dos">
                <Campo etiqueta="Aforo" tipo="number" modo="numeric" valor={form.aforo} onChange={(v) => { setForm({ ...form, aforo: v }); }} error={errores.capacity} ayuda="Uno solo para entradas, listas y taquilla." bloqueado={!edita} />
                <Campo etiqueta="Cupo de taquilla" tipo="number" modo="numeric" valor={form.cupo} onChange={(v) => { setForm({ ...form, cupo: v }); }} error={errores.box_office_quota} ayuda="Para la puerta, también sin conexión." bloqueado={!edita} />
            </div>
            <div className="rv-ev-medio">
                <CampoDiaHora etiqueta="Apertura de puertas" valor={form.puertas} onChange={(v) => { setForm({ ...form, puertas: v }); }} error={errores.door_opens_at} bloqueado={!edita} />
            </div>
            {lineas.length > 0 && <Lineas items={lineas} />}
        </div>
    );
}

export function Publicar({ api, negocio, datos, soloLectura, permisos, setAccion, ponerEvento, irPestana, recargarFicha }: PropsPestana) {
    const e = datos.evento;
    const avisar = useAvisoBreve();
    const [fallos, setFallos] = useState<{ field: string; code: string; message?: string }[] | null>(null);
    const [ocupado, setOcupado] = useState(false);
    const [noSeDespublica, setNoSeDespublica] = useState<string | null>(null);
    const lista = requisitos(e, datos.tipos, fallos);
    const faltan = lista.filter((r) => !r.ok).length;
    const puedePublicar = e.status === 'draft' && permisos.publica && !soloLectura;

    const publicar = () => {
        setOcupado(true);
        api.publishEvent(negocio, e.id, datos.version)
            .then((r) => {
                setOcupado(false);
                setFallos(null);
                ponerEvento(r.datos, r.version);
                avisar({ tipo: 'ok', texto: 'Evento publicado.' });
            })
            .catch((err: unknown) => {
                setOcupado(false);
                if (err instanceof ErrorApi && err.codigo === 'validation_failed' && err.errores.length > 0) {
                    setFallos(err.errores);
                    return;
                }
                if (err instanceof ErrorApi && err.codigo === 'version_conflict') {
                    recargarFicha();
                }
                avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) });
            });
    };

    const despublicar = () => {
        setOcupado(true);
        api.unpublishEvent(negocio, e.id, datos.version)
            .then((r) => {
                setOcupado(false);
                ponerEvento(r.datos, r.version);
                avisar({ tipo: 'ok', texto: 'El evento vuelve a borrador.' });
            })
            .catch((err: unknown) => {
                setOcupado(false);
                if (err instanceof ErrorApi && err.estado === 409) {
                    setNoSeDespublica(err.problema.detail ?? 'Hay entradas vendidas o pendientes de pago.');
                    return;
                }
                avisar({ tipo: 'error', texto: avisoBreveDeError(err) ?? textoErrorGuardar(err) });
            });
    };

    useEffect(() => {
        setAccion(puedePublicar ? { texto: 'Publicar', icono: 'megaphone', onAccion: publicar, ocupado, ocupadoTexto: 'Publicando…' } : null);
        return () => { setAccion(null); };
        // `publicar` cambia en cada pintada; basta con lo que se ve.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [puedePublicar, ocupado, datos.version]);

    if (e.status === 'published') {
        const bloqueado = noSeDespublica ?? (hayVendidas(e) ? 'Hay entradas vendidas o pendientes de pago: cancélalas con reembolso o cancela el evento.' : null);
        return (
            <div className="rv-ev-form">
                <Aviso tono="negro" icono="megaphone" titulo={`Publicado${e.published_at == null ? '' : ` · ${diaYHoraEn(e.published_at, e.timezone).toLocaleLowerCase('es')}`}`}>
                    {e.visibility === 'unlisted' ? 'Solo con enlace: no sale en la cartelera, pero se compra con el enlace.' : 'Se ve en tu web, el widget y el asistente, y los RRPP pueden compartir sus enlaces.'}
                </Aviso>
                {bloqueado === null ? (
                    permisos.publica && !soloLectura && (
                        <div className="rv-ev-bloque">
                            <Boton tipo="secundario" icono="eye-off" ocupado={ocupado} ocupadoTexto="Despublicando…" onClick={despublicar}>
                                Despublicar
                            </Boton>
                            <span className="rv-ev-texto">Vuelve a borrador y deja de venderse. Solo sin entradas vendidas ni pedidos pendientes de pago.</span>
                        </div>
                    )
                ) : (
                    <Aviso
                        tono="gris"
                        icono="lock"
                        titulo="No se puede despublicar."
                        accion={
                            permisos.cancela ? (
                                <Boton tipo="secundario" icono="calendar-x" onClick={() => { irPestana('cancelar'); }}>
                                    Cancelar el evento
                                </Boton>
                            ) : undefined
                        }
                    >
                        {bloqueado}
                    </Aviso>
                )}
            </div>
        );
    }

    return (
        <div className="rv-ev-form">
            <Ceja>Antes de publicar</Ceja>
            <p className="rv-ev-texto">{faltan === 0 ? 'Todo listo para publicar.' : faltan === 1 ? 'Falta una cosa para publicar.' : `Faltan ${String(faltan)} cosas para publicar.`}</p>
            <ul className="rv-ev-requisitos">
                {lista.map((r) => (
                    <li key={r.clave} className={r.ok ? '' : 'rv-ev-requisitos--falla'}>
                        <span className="rv-ev-requisitos__marca" aria-hidden="true">
                            <Icono nombre={r.ok ? 'check' : 'x'} />
                        </span>
                        <span className="rv-en-celda">
                            <strong>{r.titulo}</strong>
                            <small>{r.texto}</small>
                        </span>
                        {!r.ok && r.ir !== undefined && (
                            <Boton tipo="secundario" icono="arrow-right" onClick={() => { if (r.ir !== undefined) { irPestana(r.ir); } }}>
                                {`Ir a ${r.ir === 'entradas' ? 'Entradas' : r.ir === 'puerta' ? 'Puerta' : 'Datos'}`}
                            </Boton>
                        )}
                    </li>
                ))}
            </ul>
            <p className="rv-ev-texto rv-ev-texto--nota">
                <Icono nombre="info" />
                <span>{soloLectura ? 'El negocio está en solo lectura: ahora no se puede publicar.' : 'Los recordatorios y los enlaces de los RRPP se activan solos al publicar.'}</span>
            </p>
        </div>
    );
}
